import { useQuery } from "@tanstack/react-query";
import { DelayByShift } from "../models/DelayByShift";
import { DelaysSummary } from "../models/DelaysSummary";
import { getDelay, getDelays } from "../repositories/DelaysManagerRepository";

interface DelaysResult {
	rows: DelayByShift[];
	summary: DelaysSummary;
}

export const DELAYS_QUERY_KEYS = {
	all: ["delays"] as const,
	list: (
		shiftId: number | null | undefined,
		startDate: string | null,
		endDate: string | null,
		assetId: number | null,
		crew: string | null,
		delayCategoryId: number | null,
		delayCodeId: number | null,
		productTypeId: number | null,
		productSizeId: number | null,
	) =>
		[
			"delays",
			shiftId,
			startDate,
			endDate,
			assetId,
			crew,
			delayCategoryId,
			delayCodeId,
			productTypeId,
			productSizeId,
		] as const,
	byId: (delayId: number | null) => ["delays", "detail", delayId] as const,
};

interface UseDelaysProps {
	shiftId?: number | null;
	startDate?: string | null;
	endDate?: string | null;
	delayAreaAssetId?: number | null;
	crew?: string | null;
	delayCategoryId?: number | null;
	delayCodeId?: number | null;
	productTypeId?: number | null;
	productSizeId?: number | null;
	enabled?: boolean;
	refetchInterval?: number | false;
}

export const useDelays = ({
	shiftId = null,
	startDate = null,
	endDate = null,
	delayAreaAssetId = null,
	crew = null,
	delayCategoryId = null,
	delayCodeId = null,
	productTypeId = null,
	productSizeId = null,
	enabled = true,
	refetchInterval = false,
}: UseDelaysProps) => {
	return useQuery<DelaysResult, Error>({
		queryKey: DELAYS_QUERY_KEYS.list(
			shiftId,
			startDate,
			endDate,
			delayAreaAssetId,
			crew,
			delayCategoryId,
			delayCodeId,
			productTypeId,
			productSizeId,
		),
		queryFn: () =>
			getDelays({
				shiftId,
				startDate,
				endDate,
				delayAreaAssetId,
				crew,
				delayCategoryId,
				delayCodeId,
				productTypeId,
				productSizeId,
			}),
		enabled,
		refetchInterval,
	});
};

interface UseDelayByIdProps {
	delayId: number | null;
}

export const useDelayById = ({ delayId }: UseDelayByIdProps) => {
	return useQuery<DelayByShift[], Error>({
		queryKey: DELAYS_QUERY_KEYS.byId(delayId),
		queryFn: () => getDelay({ delayId }),
		enabled: !!delayId && delayId > 0,
	});
};
